/**
 * Retro chiptune audio synthesized in-browser via Web Audio API.
 * No audio files needed — square waves, noise bursts, and simple envelopes.
 *
 * Music: looping pentatonic-ish bass + lead melody for a "forest arcade" feel.
 */

type SfxName =
  | "catch_common"
  | "catch_choice"
  | "catch_rare"
  | "medicinal"
  | "inedible"
  | "miss"
  | "poison"
  | "deadly"
  | "menu_move"
  | "menu_select"
  | "game_over"
  | "level_up";

const NOTE: Record<string, number> = {
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, B5: 987.77,
  C6: 1046.5, D6: 1174.66, E6: 1318.51,
};

export class Audio {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private _muted = false;
  private _musicVolume = 0.18;
  private _sfxVolume = 0.45;

  init() {
    if (this.ctx) return;
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    this.ctx = new Ctor();
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.musicGain.gain.value = this._muted ? 0 : this._musicVolume;
    this.sfxGain.gain.value = this._muted ? 0 : this._sfxVolume;
    this.musicGain.connect(this.ctx.destination);
    this.sfxGain.connect(this.ctx.destination);
  }

  setMuted(muted: boolean) {
    this._muted = muted;
    if (this.musicGain) this.musicGain.gain.value = muted ? 0 : this._musicVolume;
    if (this.sfxGain) this.sfxGain.gain.value = muted ? 0 : this._sfxVolume;
  }
  isMuted() { return this._muted; }

  // ---------- SFX ----------

  private blip(freq: number, dur: number, type: OscillatorType = "square", vol = 0.4) {
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain).connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private slide(f1: number, f2: number, dur: number, type: OscillatorType = "square", vol = 0.4) {
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f1, t);
    osc.frequency.exponentialRampToValueAtTime(f2, t + dur);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain).connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noise(dur: number, vol = 0.3, hp = 800) {
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime;
    const bufferSize = Math.floor(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = hp;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter).connect(gain).connect(this.sfxGain);
    src.start(t);
    src.stop(t + dur);
  }

  play(name: SfxName) {
    this.init();
    switch (name) {
      case "catch_common":
        this.blip(NOTE.C5, 0.06, "square", 0.35);
        setTimeout(() => this.blip(NOTE.E5, 0.08, "square", 0.35), 50);
        break;
      case "catch_choice":
        this.blip(NOTE.G5, 0.06, "square", 0.4);
        setTimeout(() => this.blip(NOTE.C6, 0.06, "square", 0.4), 60);
        setTimeout(() => this.blip(NOTE.E6, 0.1, "square", 0.4), 120);
        break;
      case "catch_rare":
        this.blip(NOTE.C5, 0.05, "triangle", 0.4);
        setTimeout(() => this.blip(NOTE.E5, 0.05, "triangle", 0.4), 50);
        setTimeout(() => this.blip(NOTE.G5, 0.05, "triangle", 0.4), 100);
        setTimeout(() => this.blip(NOTE.C6, 0.05, "triangle", 0.45), 150);
        setTimeout(() => this.blip(NOTE.E6, 0.2, "triangle", 0.5), 200);
        break;
      case "medicinal":
        // healing chime — rising triad with bell-like triangle waves
        this.blip(NOTE.E5, 0.08, "triangle", 0.4);
        setTimeout(() => this.blip(NOTE.A5, 0.08, "triangle", 0.4), 70);
        setTimeout(() => this.blip(NOTE.E6, 0.18, "triangle", 0.45), 140);
        break;
      case "inedible":
        // dull thud — short low square, no pitch fanfare
        this.blip(NOTE.D3, 0.08, "square", 0.18);
        break;
      case "miss":
        this.blip(NOTE.A3, 0.12, "triangle", 0.25);
        break;
      case "poison":
        // dissonant descending warble
        this.slide(NOTE.G4, NOTE.E3, 0.4, "sawtooth", 0.35);
        this.noise(0.15, 0.15, 200);
        break;
      case "deadly":
        // final boss vibe — long descending growl + noise burst
        this.slide(NOTE.A3, NOTE.C3, 0.9, "sawtooth", 0.5);
        this.noise(0.8, 0.3, 100);
        setTimeout(() => this.blip(NOTE.C3, 0.4, "sawtooth", 0.5), 200);
        break;
      case "menu_move":
        this.blip(NOTE.E5, 0.04, "square", 0.25);
        break;
      case "menu_select":
        this.blip(NOTE.C5, 0.05, "square", 0.35);
        setTimeout(() => this.blip(NOTE.G5, 0.08, "square", 0.35), 50);
        break;
      case "game_over":
        this.blip(NOTE.C4, 0.18, "square", 0.4);
        setTimeout(() => this.blip(NOTE.B3, 0.18, "square", 0.4), 200);
        setTimeout(() => this.blip(NOTE.A3, 0.18, "square", 0.4), 400);
        setTimeout(() => this.blip(NOTE.G3, 0.4, "square", 0.4), 600);
        break;
      case "level_up":
        this.blip(NOTE.C5, 0.08, "square", 0.4);
        setTimeout(() => this.blip(NOTE.E5, 0.08, "square", 0.4), 90);
        setTimeout(() => this.blip(NOTE.G5, 0.08, "square", 0.4), 180);
        setTimeout(() => this.blip(NOTE.C6, 0.25, "square", 0.45), 270);
        break;
    }
  }

  // ---------- Music (looped chiptune) ----------

  /**
   * Forest-arcade looping music. Bouncy C-major pentatonic groove:
   *   bass: walking root/fifth pattern in C with offbeat lift
   *   lead: syncopated pentatonic riff with arpeggio runs
   *   drums: kick on beats 1+3, snare on 2+4, hat on every offbeat
   */
  startMusic() {
    this.init();
    if (this.musicTimer !== null || !this.ctx) return;
    this.musicStep = 0;
    const tickMs = 125; // ~240 BPM sixteenth notes — fast & bouncy
    this.musicTimer = window.setInterval(() => this.musicTick(), tickMs);
    this.musicTick();
  }

  stopMusic() {
    if (this.musicTimer !== null) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
  }

  private musicTick() {
    if (!this.ctx || !this.musicGain) return;

    // 32-step loop (~4 seconds). Bouncy C-major pentatonic with chord changes.
    // Chord progression (per 8 steps): C — Am — F — G
    const bassSeq = [
      NOTE.C3, 0, NOTE.G3, 0, NOTE.C3, NOTE.G3, NOTE.E3, NOTE.G3,
      NOTE.A3, 0, NOTE.E3, 0, NOTE.A3, NOTE.E3, NOTE.C4, NOTE.E3,
      NOTE.F3, 0, NOTE.C4, 0, NOTE.F3, NOTE.C4, NOTE.A3, NOTE.C4,
      NOTE.G3, 0, NOTE.D4, 0, NOTE.G3, NOTE.D4, NOTE.B3, NOTE.D4,
    ];
    // Lead: cheerful pentatonic riff with arpeggio sparkle
    const leadSeq = [
      NOTE.E5, 0, NOTE.G5, NOTE.E5, 0, NOTE.C5, NOTE.D5, NOTE.E5,
      NOTE.C5, NOTE.E5, NOTE.A5, NOTE.E5, 0, NOTE.C5, NOTE.A4, NOTE.C5,
      NOTE.F5, 0, NOTE.A5, NOTE.F5, NOTE.C5, NOTE.A4, NOTE.C5, NOTE.F5,
      NOTE.G5, NOTE.D5, NOTE.B4, NOTE.D5, NOTE.G5, 0, NOTE.E5, NOTE.D5,
    ];
    // Harmony layer: chord stab every 4 steps for fullness
    const harmonySeq = [
      NOTE.G4, 0, 0, 0, NOTE.E4, 0, 0, 0,
      NOTE.E4, 0, 0, 0, NOTE.C4, 0, 0, 0,
      NOTE.A4, 0, 0, 0, NOTE.F4, 0, 0, 0,
      NOTE.B4, 0, 0, 0, NOTE.D4, 0, 0, 0,
    ];

    const step = this.musicStep;
    const bassNote = bassSeq[step % bassSeq.length];
    const leadNote = leadSeq[step % leadSeq.length];
    const harmonyNote = harmonySeq[step % harmonySeq.length];

    if (bassNote > 0) this.playMusicNote(bassNote, "triangle", 0.18, 0.22);
    if (leadNote > 0) this.playMusicNote(leadNote, "square", 0.12, 0.11);
    if (harmonyNote > 0) this.playMusicNote(harmonyNote, "triangle", 0.30, 0.07);

    // Drum kit: kick on 0/8/16/24, snare on 4/12/20/28, hat every offbeat
    const beat = step % 8;
    if (beat === 0 || beat === 4) this.playMusicKick();
    if (beat === 2 || beat === 6) this.playMusicSnare();
    if (step % 2 === 1) this.playMusicNoise(0.025, 0.035);

    this.musicStep = (this.musicStep + 1) % 32;
  }

  private playMusicKick() {
    if (!this.ctx || !this.musicGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.12);
    gain.gain.setValueAtTime(0.32, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.connect(gain).connect(this.musicGain);
    osc.start(t);
    osc.stop(t + 0.2);
  }

  private playMusicSnare() {
    if (!this.ctx || !this.musicGain) return;
    const t = this.ctx.currentTime;
    const dur = 0.08;
    const size = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, size, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < size; i++) data[i] = (Math.random() * 2 - 1) * 0.7;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filt = this.ctx.createBiquadFilter();
    filt.type = "bandpass";
    filt.frequency.value = 1800;
    filt.Q.value = 0.7;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.14, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filt).connect(gain).connect(this.musicGain);
    src.start(t);
    src.stop(t + dur);
  }

  private playMusicNote(freq: number, type: OscillatorType, dur: number, vol: number) {
    if (!this.ctx || !this.musicGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain).connect(this.musicGain);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private playMusicNoise(dur: number, vol: number) {
    if (!this.ctx || !this.musicGain) return;
    const t = this.ctx.currentTime;
    const size = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, size, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < size; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filt = this.ctx.createBiquadFilter();
    filt.type = "highpass";
    filt.frequency.value = 4000;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filt).connect(gain).connect(this.musicGain);
    src.start(t);
    src.stop(t + dur);
  }
}

export const audio = new Audio();
